/**
 * Support Inbox Page – enterprise three-column command center
 */
import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@respectly/ui';
import { Button } from '@respectly/ui';
import {
  Headphones,
  RefreshCw,
  ChevronLeft,
  PanelRightClose,
  PanelRightOpen,
  UserPlus,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Circle,
} from 'lucide-react';
import { useAuth } from '@respectly/utils/auth';
import { API_ENDPOINTS } from '@respectly/api-client';
import { useSupportStore } from '../../stores/useSupportStore';
import {
  SupportFilters,
  SupportRequestList,
  SupportThread,
  SupportReplyComposer,
  AccountQuickInfo,
  AccountQuickInfoSkeleton,
  AIFlagsPanel,
  SupportStatusBadge,
  SupportTicketDetails,
  AssignTicketModal,
} from '../../components/support';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export function SupportInbox() {
  const { getAccessToken } = useAuth();
  const {
    requests,
    pagination,
    isLoadingList,
    activeRequest,
    messages,
    isLoadingMessages,
    queueSummary,
    aiFlags,
    isLoadingAiFlags,
    isPerformingAction,
    loadRequests,
    loadQueueSummary,
    loadAiFlags,
    selectRequest,
    clearActiveRequest,
    addMessage,
    assignRequest,
    resolveRequest,
    reopenRequest,
    closeRequest,
    reviewAiFlag,
  } = useSupportStore();

  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadRequests();
    loadQueueSummary();
    loadAiFlags();
  }, [loadRequests, loadQueueSummary, loadAiFlags]);

  const handleSendMessage = async (
    content: string,
    isInternal: boolean,
    attachments?: Array<{ url: string; fileName: string; fileSize?: number }>
  ) => {
    if (!activeRequest) return false;
    return await addMessage(activeRequest.id, { content, isInternal, attachments });
  };

  const handleUpload = useCallback(
    async (file: File): Promise<{ url: string; fileName: string; fileSize?: number }> => {
      if (!activeRequest) throw new Error('No request selected');
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication required');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(
        `${API_URL}${API_ENDPOINTS.SUPER_ADMIN.SUPPORT.UPLOAD(activeRequest.id)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message ?? 'Upload failed');
      if (!json.success || !json.data) throw new Error('Invalid upload response');
      return { url: json.data.url, fileName: json.data.fileName, fileSize: json.data.fileSize };
    },
    [activeRequest, getAccessToken]
  );

  const handleReviewFlag = async (id: string, action: 'reviewed' | 'dismissed' | 'actioned') => {
    await reviewAiFlag(id, { status: action });
  };

  const showMobileList = isMobile && !activeRequest;
  const showMobileDetail = isMobile && activeRequest;

  return (
    <motion.div
      className="h-[calc(100vh-theme(spacing.16))] flex flex-col bg-zinc-50 dark:bg-zinc-950"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Header */}
      <div className="shrink-0 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between px-5 h-14">
          <div className="flex items-center gap-3">
            {showMobileDetail && (
              <Button variant="ghost" size="sm" onClick={clearActiveRequest} className="md:hidden h-8 w-8 p-0 rounded-lg">
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}

            <div className="h-8 w-8 rounded-lg bg-blue-500/10 dark:bg-blue-500/15 ring-1 ring-blue-500/20 flex items-center justify-center">
              <Headphones className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            </div>

            <h1 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 tracking-tight">
              Support Inbox
            </h1>

            {/* Live queue stats */}
            {queueSummary && (
              <div className="hidden sm:flex items-center gap-1.5 ml-2">
                <StatChip
                  value={queueSummary.open}
                  label="open"
                  colorClass="bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 ring-blue-500/20"
                />
                {queueSummary.inProgress > 0 && (
                  <StatChip
                    value={queueSummary.inProgress}
                    label="in progress"
                    colorClass="bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 ring-amber-500/20"
                  />
                )}
                {queueSummary.urgent > 0 && (
                  <StatChip
                    value={queueSummary.urgent}
                    label="urgent"
                    colorClass="bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 ring-red-500/20"
                    pulse
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadRequests()}
              disabled={isLoadingList}
              className="h-8 w-8 p-0 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              <RefreshCw className={cn('h-3.5 w-3.5 text-zinc-500', isLoadingList && 'animate-spin')} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSidebar(!showSidebar)}
              className="hidden xl:flex h-8 w-8 p-0 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              {showSidebar
                ? <PanelRightClose className="h-3.5 w-3.5 text-zinc-500" />
                : <PanelRightOpen className="h-3.5 w-3.5 text-zinc-500" />
              }
            </Button>
          </div>
        </div>

        {/* Filters bar */}
        {!showMobileDetail && (
          <div className="px-5 pb-3">
            <SupportFilters compact={isMobile} />
          </div>
        )}
      </div>

      {/* Three-column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Request list */}
        {(showMobileList || !isMobile) && (
          <div className={cn(
            'border-r border-zinc-200 dark:border-zinc-800 overflow-y-auto bg-white dark:bg-zinc-900 flex flex-col',
            isMobile ? 'w-full' : 'w-80 shrink-0'
          )}>
            <SupportRequestList
              requests={requests}
              activeId={activeRequest?.id}
              onSelect={selectRequest}
              isLoading={isLoadingList}
            />

            {pagination && pagination.totalPages > 1 && (
              <div className="shrink-0 border-t border-zinc-200 dark:border-zinc-800 px-4 py-3 flex items-center justify-between">
                <span className="text-[11px] text-zinc-400 font-mono">
                  {pagination.page}/{pagination.totalPages}
                </span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" disabled={pagination.page === 1} onClick={() => loadRequests(pagination.page - 1)} className="h-6 px-2 text-[11px] rounded">
                    Prev
                  </Button>
                  <Button variant="ghost" size="sm" disabled={pagination.page === pagination.totalPages} onClick={() => loadRequests(pagination.page + 1)} className="h-6 px-2 text-[11px] rounded">
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Center: Thread */}
        {(showMobileDetail || !isMobile) && (
          <div className="flex-1 flex flex-col overflow-hidden bg-zinc-50 dark:bg-zinc-950">
            {activeRequest ? (
              <>
                {/* Quick actions bar */}
                <div className="shrink-0 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-2 flex items-center gap-2">
                  <SupportStatusBadge status={activeRequest.status} size="sm" />
                  <div className="flex-1" />

                  {(activeRequest.status === 'open' || activeRequest.status === 'in_progress') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAssignModal(true)}
                      disabled={isPerformingAction}
                      className="h-7 px-2.5 text-xs gap-1.5 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg"
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Assign
                    </Button>
                  )}

                  {(activeRequest.status === 'open' || activeRequest.status === 'in_progress') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2.5 text-xs gap-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg"
                      onClick={() => resolveRequest(activeRequest.id)}
                      disabled={isPerformingAction}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Resolve
                    </Button>
                  )}

                  {activeRequest.status === 'resolved' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => reopenRequest(activeRequest.id)}
                        disabled={isPerformingAction}
                        className="h-7 px-2.5 text-xs gap-1.5 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Reopen
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => closeRequest(activeRequest.id)}
                        disabled={isPerformingAction}
                        className="h-7 px-2.5 text-xs gap-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                        Close
                      </Button>
                    </>
                  )}
                </div>

                <SupportThread
                  request={activeRequest}
                  messages={messages}
                  isLoading={isLoadingMessages}
                  className="flex-1 overflow-hidden"
                />

                {activeRequest.status !== 'closed' && (
                  <SupportReplyComposer
                    onSubmit={handleSendMessage}
                    onUpload={handleUpload}
                    isDisabled={isPerformingAction}
                  />
                )}
              </>
            ) : (
              <EmptyState />
            )}
          </div>
        )}

        {/* Right: Sidebar */}
        {showSidebar && !isMobile && (
          <motion.div
            className="w-64 shrink-0 border-l border-zinc-200 dark:border-zinc-800 overflow-y-auto bg-white dark:bg-zinc-900"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {activeRequest && (
              <SupportTicketDetails request={activeRequest} />
            )}
            {activeRequest?.tenant ? (
              <AccountQuickInfo tenant={activeRequest.tenant as any} />
            ) : (
              <AccountQuickInfoSkeleton />
            )}
            <AIFlagsPanel
              flags={aiFlags}
              isLoading={isLoadingAiFlags}
              onReview={handleReviewFlag}
            />
          </motion.div>
        )}
      </div>

      {/* Assign Ticket Modal */}
      {activeRequest && (
        <AssignTicketModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          currentAssignedToId={activeRequest.assignedToId}
          onAssign={(userId) => assignRequest(activeRequest.id, { assignedToId: userId })}
          isAssigning={isPerformingAction}
        />
      )}
    </motion.div>
  );
}

// ── Stat Chip ──────────────────────────────────────────────────────────────────

function StatChip({
  value,
  label,
  colorClass,
  pulse,
}: {
  value: number;
  label: string;
  colorClass: string;
  pulse?: boolean;
}) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset',
      colorClass
    )}>
      {pulse && <Circle className="h-2 w-2 fill-current animate-pulse" />}
      <span className="font-mono">{value}</span>
      <span className="font-normal opacity-80">{label}</span>
    </span>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500/15 to-indigo-600/15 ring-1 ring-blue-500/20 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/10">
        <Headphones className="h-7 w-7 text-blue-500 dark:text-blue-400" />
      </div>
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 tracking-tight mb-1">
        Select a request
      </h3>
      <p className="text-xs text-zinc-500 dark:text-zinc-400 max-w-[200px] leading-relaxed">
        Choose a support request from the list to view the conversation.
      </p>
    </div>
  );
}
